import React, {useEffect, useState} from 'react';
import {IonAlert, IonCol, IonLoading, IonRow, IonToast} from '@ionic/react';
import {useTranslation} from 'react-i18next';
import {FormProvider, useForm, useWatch} from "react-hook-form";
import {useEvent} from "@hooks/useEvent";
import {useHistory, useLocation} from "react-router-dom";
import axios from "axios";
import moment from "moment";
import Accordion from "@components/accordion/accordion";
import RideFormFields from "@app/travelAllowance/ride/components/rideFormFields.component";
import BackRideFormFields from "@app/travelAllowance/ride/components/backRideFormFields.component";
import CommentForCoordinatorModal from "@app/travelAllowance/ride/components/commentForCoordinatorModal";
import TravelAllowanceFormSkeleton from "@app/travelAllowance/common/components/travelAllowanceFormSkeleton.component";
import Form from "@components/form";
import {StyledButton, StyledButtonWrapper, StyledTravelAllowanceContent, StyledWarningInfo} from "@app/travelAllowance/travelAllowance.style";
import {StyledInput} from "@components/form/input/input.style";
import {PlaceOfService, RideRoute} from "@models/travelAllowance/rideRoute";
import {Car} from "@models/travelAllowance/car";
import {BackRideForm, Coordinator, Passenger, PassengerFormField, Ride, RideChange, RideForm, RideFormData, RideFormDataForApi} from "@models/travelAllowance/ride";
import {RideMatrix} from "@models/travelAllowance/rideMatrix";
import {getRideRoutes} from "@services/travelAllowance/rideRoute.service";
import {getCars} from "@services/travelAllowance/car.service";
import {addRide, editRide, getPossiblePassengers, handleCommonRideErrorResponseMessage, validateRide} from "@services/travelAllowance/ride.service";
import {getRideMatrix} from "@services/travelAllowance/rideMatrix.service";
import {ReactComponent as SaveIcon} from '@assets/images/travelAllowance/save.svg';
import {ReactComponent as WarningIcon} from '@assets/images/travelAllowance/warning.svg';
import {Links} from "@app/links";
import {RideFormContext} from "@context/rideForm.context";
import {RidePaneType} from "@enums/travelAllowance/ride";
import {EventType} from "@enums/eventType";
import StaticPane from "@components/pane/static-pane.component";
import {useAutoScrollInputs} from "@hooks/useInputAutoScroll";
import {getWorkerWorkerFormSettings} from "@services/worker.service";
import {WorkerWorkerFormSettings} from "@models/worker";
import {transformSubRoutesToFormField} from "@services/travelAllowance/subRoute.service";
import {getServerNow} from "@services/serverDate.service";

type RideAddEditPaneProps = {
    type: RidePaneType,
    topEdge?: number,
    rideDefaultValues?: Ride
    rideChanges?: RideChange
}

const RideAddEditPane: React.FC<RideAddEditPaneProps> = ({type, topEdge, rideDefaultValues, rideChanges}: RideAddEditPaneProps) => {
    const cancelToken = axios.CancelToken.source();

    const {t} = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const {dispatch} = useEvent();
    const form = useForm<RideFormData>({
        mode: 'onChange',
        defaultValues: {
            ride: {
                dateStart: rideDefaultValues?.dateStart || getServerNow().format(),
                car: rideDefaultValues?.car.id,
                route: {
                    id: rideDefaultValues?.rideRoute.id,
                    startingPlace: rideDefaultValues?.rideRoute.startingPlace,
                    destinationPlace: rideDefaultValues?.rideRoute.destinationPlace,
                    startingPlaceCountry: rideDefaultValues?.rideRoute.startingPlaceCountry,
                    destinationPlaceCountry: rideDefaultValues?.rideRoute.destinationPlaceCountry,
                    startingLatitude: rideDefaultValues?.rideRoute?.startingLatitude,
                    destinationLatitude: rideDefaultValues?.rideRoute?.destinationLatitude,
                    startingLongitude: rideDefaultValues?.rideRoute?.startingLongitude,
                    destinationLongitude: rideDefaultValues?.rideRoute?.destinationLongitude,
                    distance: rideDefaultValues?.rideRoute.distance,
                    coordinator: rideDefaultValues?.rideRoute.coordinator.id,
                    placeOfService: rideDefaultValues?.rideRoute.placeOfService.id,
                    isLocal: rideDefaultValues?.rideRoute.isLocal,
                    subRoutes: rideDefaultValues?.rideRoute.subRoutes ? transformSubRoutesToFormField(rideDefaultValues?.rideRoute.subRoutes) : [],
                },
            },
        },
    });
    const watch = useWatch({
        control: form.control, name: [
            'ride.dateStart',
            'ride.route.startingPlace',
            'ride.route.destinationPlace',
            'ride.route.startingPlaceCountry',
            'ride.route.destinationPlaceCountry',
            'ride.route.distance',
            'ride.route.placeOfService',
            'ride.route.coordinator',
            'ride.car',
            'ride.passengers'
        ]
    });

    const [loading, setLoading] = useState<boolean>(true);
    const [rideRoutes, setRideRoutes] = useState<RideRoute[]>([]);
    const [cars, setCars] = useState<Car[]>([]);
    const [rideMatrix, setRideMatrix] = useState<RideMatrix | undefined>();
    const [possiblePassengers, setPossiblePassengers] = useState<Passenger[]>([]);
    const [workerWorkerFormSettings, setWorkerWorkerFormSettings] = useState<WorkerWorkerFormSettings>();
    const [coordinator, setCoordinator] = useState<Coordinator | undefined>();
    const [placeOfService, setPlaceOfService] = useState<PlaceOfService | undefined>();

    const [isCommentForCoordinatorModalOpen, setIsCommentForCoordinatorModalOpen] = useState(false);
    const [showToast, setShowToast] = useState<boolean>(false);
    const [toast, setToast] = useState<string>('');
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [showInternationalWarning, setShowInternationalWarning] = useState<boolean>(false);

    useAutoScrollInputs(loading);

    useEffect(() => {
        const shouldFetch = location.pathname.includes(Links.travelAllowance.ride.add) || location.pathname.includes(Links.travelAllowance.ride.edit);
        if (!shouldFetch) return;

        setLoading(true);

        fetchData()
            .finally(() => {
                setCoordinator(undefined);
                setPlaceOfService(undefined);

                setLoading(false);
            })

        return () => {
            cancelToken.cancel();
        };
    }, [location.pathname]);

    useEffect(() => {
        dispatch(EventType['REFRESH.BACK_RIDE_FORM_FIELDS']);
    }, [watch]);

    const fetchData = async () => {
        const fetchFunctions = [fetchRideRoutes(), fetchCars(), fetchPossiblePassengers(), fetchRideMatrix(), fetchWorkerWorkerFormSettings()];

        try {
            await Promise.all(fetchFunctions);
        } catch (error) {
            setToast(t("travelAllowance.ride.formLoadError"));
            setShowToast(true);

            console.error(error);
        }
    };

    const fetchRideRoutes = async () => {
        const rideRoutesData = await getRideRoutes(cancelToken);

        setRideRoutes(rideRoutesData);
    }

    const fetchCars = async () => {
        const carsData = await getCars(cancelToken);

        setCars(carsData);
    }

    const fetchRideMatrix = async () => {
        const rideMatrixData = await getRideMatrix(rideDefaultValues?.dateStart, cancelToken);

        setRideMatrix(!Array.isArray(rideMatrixData) ? rideMatrixData : undefined);
    }

    const fetchPossiblePassengers = async () => {
        if (coordinator && placeOfService) {
            const passengersData = await getPossiblePassengers(coordinator.id, placeOfService.id, form.getValues().ride?.dateStart, cancelToken);

            setPossiblePassengers(passengersData);
        }
    }

    const fetchWorkerWorkerFormSettings = async () => {
        const workerWorkerFormSettingsData = await getWorkerWorkerFormSettings();

        setWorkerWorkerFormSettings(workerWorkerFormSettingsData);
    }

    const handleError = (error: any, defaultErrorMessageKey: string) => {
        const errorMessage = error.response.data.detail;
        const messageKey = handleCommonRideErrorResponseMessage(errorMessage, defaultErrorMessageKey);

        setToast(t(messageKey));
        setShowToast(true);

        console.error(error);
    };

    const submitAdd = (data: RideFormDataForApi) => {
        setShowLoader(true);

        addRide(data)
            .then(() => {
                history.replace(Links.main + Links.travelAllowance.ride.list);
            })
            .catch((error) => handleError(error, 'travelAllowance.ride.addError'))
            .finally(() => {
                setShowLoader(false);
            })
    };

    const submitEdit = (rideId: number, data: RideFormDataForApi) => {
        setShowLoader(true);

        editRide(rideId, data)
            .then(() => {
                history.replace(Links.main + Links.travelAllowance.ride.list);
            })
            .catch((error) => handleError(error, 'travelAllowance.ride.editError'))
            .finally(() => {
                setShowLoader(false);
            })
    };

    const onSubmit = ({ride, backRide, comment}: RideFormData) => {
        const rideDataForApi = prepareRideOnSubmit(ride);
        const backRideDataForApi = prepareBackRideOnSubmit(backRide);

        const data: RideFormDataForApi = {
            ...rideDataForApi,
            ...(backRideDataForApi && {backRide: backRideDataForApi}),
            ...(comment && {comment: comment}),
        };

        const editedRideId = type === RidePaneType.EDIT ? rideDefaultValues?.id : undefined;

        if (type !== RidePaneType.ADD && !editedRideId) {
            return;
        }

        const errorMessageKey = editedRideId ? 'travelAllowance.ride.editError' : 'travelAllowance.ride.addError';

        setShowLoader(true);

        validateRide({
            dateStart: data.dateStart,
            route: data.route,
            ...(editedRideId && {rideId: editedRideId}),
        })
            .then((result) => {
                if (result.frequentInternational) {
                    setShowLoader(false);
                    setShowInternationalWarning(true);

                    return;
                }

                if (editedRideId) {
                    submitEdit(editedRideId, data);

                    return;
                }

                submitAdd(data);
            })
            .catch((error) => {
                setShowLoader(false);
                handleError(error, errorMessageKey);
            });
    };

    const prepareRideOnSubmit = (ride: RideForm) => {
        const {passengers, route, ...rideRest} = ride;
        const {id, ...routeRest} = route;
        const isFixedRideRoute = rideRoutes.some(rideRoute => rideRoute.id === id);

        return {
            ...rideRest,
            ...(id && isFixedRideRoute) ? {route: id} : {route: routeRest},
            ...(passengers !== undefined && {passengers: preparePassengersOnSubmit(ride)}),
        };
    }

    const prepareBackRideOnSubmit = (backRide: BackRideForm | undefined) => {
        if (backRide) {
            const {passengers, ...rest} = backRide;

            return {
                ...rest,
                ...(passengers !== undefined && {passengers: preparePassengersOnSubmit(backRide)}),
            };
        }
    }

    const preparePassengersOnSubmit = (rideData: RideForm | BackRideForm | undefined) => {
        if (rideData !== undefined && rideData.passengers !== undefined) {
            return rideData.passengers.map((passenger: PassengerFormField) => {
                return passenger.workerId;
            })
        }
    }

    const handleCommentForCoordinatorChange = (commentForCoordinator: string) => {
        form.setValue('comment', commentForCoordinator);
    }

    return (
        <StaticPane topEdge={topEdge} marginTop={40} paddingTop={30} paddingBottom={147} hideGrabber={true}>
            {
                <StyledTravelAllowanceContent>
                    <IonLoading onDidDismiss={() => setShowLoader(false)} isOpen={showLoader}/>

                    <IonToast
                        isOpen={showToast}
                        onDidDismiss={() => setShowToast(false)}
                        message={toast}
                        duration={6000}
                        position="top"
                        color="danger"
                    />

                    <IonAlert
                        isOpen={showInternationalWarning}
                        onDidDismiss={() => setShowInternationalWarning(false)}
                        header={t('travelAllowance.ride.internationalWarning.title')}
                        message={t('travelAllowance.ride.internationalWarning.message')}
                        buttons={[
                            {
                                text: t('travelAllowance.ride.internationalWarning.confirm'),
                                handler: () => setShowInternationalWarning(false),
                            }
                        ]}
                    />
                    {loading
                        ? (
                            <TravelAllowanceFormSkeleton/>
                        )
                        : (
                            <RideFormContext.Provider value={{
                                type: type,
                                rideRoutes: rideRoutes,
                                cars: cars,
                                passengers: possiblePassengers,
                                rideDefaultValues: rideDefaultValues,
                                setCoordinator: setCoordinator,
                                coordinator: coordinator,
                                setPlaceOfService: setPlaceOfService,
                                placeOfService: placeOfService,
                                rideChanges: rideChanges,
                                rideMatrix: rideMatrix,
                                workerFormFormSettings: workerWorkerFormSettings,
                            }}>
                                <FormProvider {...form}>
                                    <Form.Container onSubmit={form.handleSubmit(onSubmit)}>
                                        {
                                            rideDefaultValues?.commentForWorker
                                                ? <StyledWarningInfo>
                                                    <div className="changes-header">
                                                        <WarningIcon/>
                                                        <span>{t('travelAllowance.ride.commentForWorker')}:</span>
                                                        <span>{rideDefaultValues.commentForWorker}</span>
                                                    </div>
                                                </StyledWarningInfo>
                                                : <></>
                                        }
                                        {
                                            rideChanges
                                                ? <StyledWarningInfo>
                                                    <div className="changes-header">
                                                        <WarningIcon/>
                                                        <span>{t('travelAllowance.ride.fieldsChangedByCoordinator')}</span>
                                                    </div>
                                                    <div className="changes-list">
                                                        {
                                                            Object.entries(rideChanges).map(([key, translation]) => {
                                                                return <span key={key}>{t(translation)}</span>;
                                                            })
                                                        }
                                                    </div>
                                                </StyledWarningInfo>
                                                : <></>
                                        }
                                        <RideFormFields/>
                                        {
                                            type === RidePaneType.ADD ? <BackRideFormFields/> : <></>
                                        }
                                        <Accordion isOpen={false} allowToggle={true}
                                                   title={t('travelAllowance.ride.commentForCoordinator')}>
                                            {
                                                <section>
                                                    <CommentForCoordinatorModal isOpen={isCommentForCoordinatorModalOpen}
                                                                                onClose={() => setIsCommentForCoordinatorModalOpen(false)}
                                                                                setValue={(value) => handleCommentForCoordinatorChange(value)}
                                                    />
                                                    <IonRow>
                                                        <IonCol size="12" className="label mt-8 required">
                                                            {t('travelAllowance.ride.commentForCoordinator')}
                                                        </IonCol>
                                                    </IonRow>
                                                    <IonRow>
                                                        <IonCol size="12" className="label">
                                                            <StyledInput
                                                                onClick={() => setIsCommentForCoordinatorModalOpen(true)}
                                                                ref={form.register()}
                                                                name={'comment'}
                                                                readOnly={true}
                                                            />
                                                        </IonCol>
                                                    </IonRow>
                                                </section>
                                            }
                                        </Accordion>
                                        <StyledButtonWrapper>
                                            <StyledButton type="submit">
                                                <div className="btn center">
                                                    <SaveIcon/>
                                                    {
                                                        rideChanges
                                                            ? <span>{t('travelAllowance.sendToVerify')}</span>
                                                            : <span>{t('travelAllowance.save')}</span>
                                                    }
                                                </div>
                                            </StyledButton>
                                        </StyledButtonWrapper>
                                    </Form.Container>
                                </FormProvider>
                            </RideFormContext.Provider>
                        )
                    }
                </StyledTravelAllowanceContent>
            }
        </StaticPane>
    );
};

export default RideAddEditPane;